// utils/exportReceipt.js
import jsPDF from "jspdf";
import dayjs from "dayjs";

/**
 * Exports a key-value receipt layout to PDF
 */
export const exportReceiptPDF = async (receiptData) => {
  const pdf = new jsPDF({ unit: "mm", format: "a6" }); // Small receipt size

  let fileHandle = null;
  if (window.showSaveFilePicker) {
    try {
      fileHandle = await window.showSaveFilePicker({
        suggestedName: `Receipt_${dayjs().format("YYYYMMDD_HHmm")}.pdf`,
        types: [{ description: "PDF File", accept: { "application/pdf": [".pdf"] } }],
      });
    } catch {
      return; // User cancelled
    }
  }

  const pageWidth = pdf.internal.pageSize.getWidth();
  let y = 10;

  // --- Header ---
  pdf.setFontSize(10);
  pdf.setFont("helvetica", "bold");
  pdf.text("BI2 Technologies - Receipt", pageWidth / 2, y, { align: "center" });
  y += 5;
  pdf.setFontSize(8);
  pdf.setFont("helvetica", "normal");
  pdf.text("ANOKA COUNTY JAIL ANOKA, MN", pageWidth / 2, y, { align: "center" });
  y += 8;

  // --- Divider ---
  pdf.setDrawColor(150);
  pdf.line(10, y, pageWidth - 10, y);
  y += 6;

  // --- Key-Value Rows ---
  pdf.setFontSize(9);
  receiptData.forEach(({ key, value }) => {
    pdf.setFont("helvetica", "bold");
    pdf.text(`${key}:`, 12, y);
    pdf.setFont("helvetica", "normal");
    pdf.text(String(value ?? ""), 55, y); // offset for value column
    y += 6;
  });

  // --- Footer Divider ---
  y += 2;
  pdf.line(10, y, pageWidth - 10, y);

  // --- Save ---
  const blob = pdf.output("blob");
  if (fileHandle) {
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
  } else {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Receipt_${dayjs().format("YYYYMMDD_HHmm")}.pdf`;
    link.click();
  }
};

/**
 * Exports a key-value receipt layout to HTML
 */
export const exportReceiptHTML = async (receiptData, title = "Receipt") => {
  const rows = receiptData
    .map(
      ({ key, value }) => `
      <tr>
        <td style="font-weight:bold; padding:6px 12px; border-bottom:1px solid #eee; width:140px; color:#555;">
          ${key}
        </td>
        <td style="padding:6px 12px; border-bottom:1px solid #eee;">
          ${value ?? ""}
        </td>
      </tr>`
    )
    .join("");

  const fullHTML = `
    <html>
      <head>
        <title>${title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #333; }
          .receipt-header { text-align: center; margin-bottom: 16px; }
          .receipt-header h2 { margin: 0; font-size: 16px; }
          .receipt-header p  { margin: 4px 0; font-size: 12px; color: #777; }
          table { width: 100%; max-width: 480px; border-collapse: collapse; }
        </style>
      </head>
      <body>
        <div class="receipt-header">
          <h2>BI2 Technologies - Receipt</h2>
          <p>ANOKA COUNTY JAIL ANOKA, MN</p>
        </div>
        <table>${rows}</table>
      </body>
    </html>`;

  try {
    if (window.showSaveFilePicker) {
      // Modern browsers — shows Save As dialog
      const fileHandle = await window.showSaveFilePicker({
        suggestedName: `Receipt_${dayjs().format("YYYYMMDD_HHmm")}.html`,
        types: [{ description: "HTML File", accept: { "text/html": [".html"] } }],
      });
      const writable = await fileHandle.createWritable();
      await writable.write(fullHTML);
      await writable.close();
    } else {
      // Fallback — direct download without Save As dialog
      const blob = new Blob([fullHTML], { type: "text/html;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Receipt_${dayjs().format("YYYYMMDD_HHmm")}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  } catch (err) {
    if (err.name !== "AbortError") {
      // AbortError = user cancelled, anything else is a real error
      console.error("HTML export failed:", err);

      // Last resort fallback if showSaveFilePicker threw unexpectedly
      const blob = new Blob([fullHTML], { type: "text/html;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Receipt_${dayjs().format("YYYYMMDD_HHmm")}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  }
};

// Add at the bottom of src/util/exportReceipt.js

/**
 * Generates a PDF blob without save dialog — used for email attachment
 */
export const generateReceiptPDFBlob = (receiptData) => {
  const pdf = new jsPDF({ unit: "mm", format: "a6" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  let y = 10;

  pdf.setFontSize(10);
  pdf.setFont("helvetica", "bold");
  pdf.text("BI2 Technologies - Receipt", pageWidth / 2, y, { align: "center" });
  y += 5;

  pdf.setFontSize(8);
  pdf.setFont("helvetica", "normal");
  pdf.text("ANOKA COUNTY JAIL ANOKA, MN", pageWidth / 2, y, { align: "center" });
  y += 8;

  pdf.setDrawColor(150);
  pdf.line(10, y, pageWidth - 10, y);
  y += 6;

  pdf.setFontSize(9);
  receiptData.forEach(({ key, value }) => {
    pdf.setFont("helvetica", "bold");
    pdf.text(`${key}:`, 12, y);
    pdf.setFont("helvetica", "normal");
    pdf.text(String(value ?? ""), 55, y);
    y += 6;
  });

  y += 2;
  pdf.line(10, y, pageWidth - 10, y);

  return pdf.output("blob"); // no save dialog, just returns blob
};

/**
 * Converts a blob to base64 string — used for email attachment
 */
export const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });